import * as React from 'react';

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */
function createContext(options) {
    if (options === void 0) { options = {}; }
    var _a = options.strict, strict = _a === void 0 ? true : _a, _b = options.errorMessage, errorMessage = _b === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _b, name = options.name;
    var Context = React.createContext(undefined);
    Context.displayName = name;
    function useContext() {
        var _a;
        var context = React.useContext(Context);
        if (!context && strict) {
            var error = new Error(errorMessage);
            error.name = 'ContextError';
            (_a = Error.captureStackTrace) === null || _a === void 0 ? void 0 : _a.call(Error, error, useContext);
            throw error;
        }
        return context;
    }
    return [
        Context.Provider,
        useContext,
        Context,
    ];
}

var isBrowser = typeof window !== 'undefined';

var getInitialState = function (query, defaultState) {
    // Prevent a React hydration mismatch when a default value is provided by not defaulting to window.matchMedia(query).matches.
    if (defaultState !== undefined) {
        return defaultState;
    }
    if (isBrowser) {
        return window.matchMedia(query).matches;
    }
    // A default value has not been provided, and you are rendering on the server, warn of a possible hydration mismatch when defaulting to false.
    if (process.env.NODE_ENV !== 'production') {
        console.warn('`useMedia` When server side rendering, defaultState should be defined to prevent a hydration mismatches.');
    }
    return false;
};
var useMedia = function (query, defaultState) {
    var _a = React.useState(getInitialState(query, defaultState)), state = _a[0], setState = _a[1];
    React.useEffect(function () {
        var mounted = true;
        var mql = window.matchMedia(query);
        var onChange = function () {
            if (!mounted) {
                return;
            }
            setState(!!mql.matches);
        };
        mql.addListener(onChange);
        setState(mql.matches);
        return function () {
            mounted = false;
            mql.removeListener(onChange);
        };
    }, [query]);
    return state;
};

var useDisclosure = function (props) {
    if (props === void 0) { props = {}; }
    var onClose = props.onClose, onOpen = props.onOpen, isOpen = props.isOpen;
    var _a = React.useState(isOpen || false), isOpenState = _a[0], setIsOpen = _a[1];
    var onCloseAction = React.useCallback(function () {
        setIsOpen(false);
        onClose && onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onOpenAction = React.useCallback(function () {
        setIsOpen(true);
        onOpen && onOpen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onToggle = React.useCallback(function () {
        var action = isOpenState ? onCloseAction : onOpenAction;
        action && action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenState]);
    return {
        isOpen: !!isOpenState,
        onOpen: onOpen,
        onClose: onClose,
        onToggle: onToggle,
    };
};

export { createContext, isBrowser, useDisclosure, useMedia };
